import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';
import api from '@/lib/api';
import { signInWithGoogle as firebaseSignInWithGoogle } from '@/lib/firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const resp = await api.get('/auth/subscription-status');
      setSubscriptionStatus(resp.data);
    } catch {
      setSubscriptionStatus(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('ihostpms_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const resp = await authAPI.me();
      setUser(resp.data);
      // Fetch subscription status after successful auth
      fetchSubscriptionStatus();
    } catch {
      localStorage.removeItem('ihostpms_token');
      localStorage.removeItem('ihostpms_user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [fetchSubscriptionStatus]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const resp = await authAPI.login({ email, password });
    const { token, user: userData, pending } = resp.data;
    localStorage.setItem('ihostpms_token', token);
    localStorage.setItem('ihostpms_user', JSON.stringify(userData));
    setUser(userData);
    fetchSubscriptionStatus();
    return { ...userData, pending };
  };

  const signup = async (data) => {
    const resp = await authAPI.signup(data);
    const { token, user: userData } = resp.data;
    localStorage.setItem('ihostpms_token', token);
    localStorage.setItem('ihostpms_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async (sessionIdOrToken) => {
    // Try Firebase popup first, fall back to session ID for backwards compat
    let idToken = sessionIdOrToken;
    if (!idToken) {
      // Firebase popup flow
      const result = await firebaseSignInWithGoogle();
      idToken = result.idToken;
    }
    const resp = await authAPI.googleCallback(idToken);
    const { token, user: userData, pending } = resp.data;
    localStorage.setItem('ihostpms_token', token);
    localStorage.setItem('ihostpms_user', JSON.stringify(userData));
    setUser(userData);
    fetchSubscriptionStatus();
    return { ...userData, pending };
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem('ihostpms_token');
    localStorage.removeItem('ihostpms_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      subscriptionStatus,
      refreshSubscription: fetchSubscriptionStatus,
      login,
      signup,
      loginWithGoogle,
      logout,
      refreshAuth: checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
